// Local Storage Manager for Group Preferences

const LAST_SELECTED_GROUP_KEY = 'cosavorrei_last_selected_group';

export interface UserGroupPreference {
  userId: number;
  lastSelectedGroupSlug: string;
  timestamp: number;
}

export class LocalStorageManager {
  /**
   * Set the last selected group for a user
   */
  static setLastSelectedGroup(userId: number, groupSlug: string): void {
    try {
      const preference: UserGroupPreference = {
        userId,
        lastSelectedGroupSlug: groupSlug,
        timestamp: Date.now()
      };
      
      localStorage.setItem(LAST_SELECTED_GROUP_KEY, JSON.stringify(preference));
    } catch (error) {
      console.warn('Failed to save group preference to localStorage:', error);
    }
  }

  /**
   * Get the last selected group for a user
   */
  static getLastSelectedGroup(userId: number): string | null {
    try {
      const stored = localStorage.getItem(LAST_SELECTED_GROUP_KEY);
      if (!stored) return null;

      const preference: UserGroupPreference = JSON.parse(stored);
      
      // Check if the preference is for the current user
      if (preference.userId !== userId) {
        return null;
      }

      // Check if the preference is not too old (30 days)
      const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
      if (preference.timestamp < thirtyDaysAgo) {
        this.clearGroupPreference();
        return null;
      }

      return preference.lastSelectedGroupSlug;
    } catch (error) {
      console.warn('Failed to read group preference from localStorage:', error);
      return null;
    }
  }

  /**
   * Clear the group preference from localStorage
   */
  static clearGroupPreference(): void {
    try {
      localStorage.removeItem(LAST_SELECTED_GROUP_KEY);
    } catch (error) {
      console.warn('Failed to clear group preference from localStorage:', error);
    }
  }

  /**
   * Check if a group slug is valid for the current user
   */
  static isValidGroupForUser(userId: number, groupSlug: string, availableGroups: string[]): boolean {
    const lastSelected = this.getLastSelectedGroup(userId);
    return lastSelected === groupSlug && availableGroups.includes(groupSlug);
  }
}